import { useMemo, useState } from 'react';
import BarcodeScanner from '../../components/BarcodeScanner';
import { Button, Input, Select } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { fmtNumber } from '../../lib/format';
import { toast } from '../../lib/toast';
import { norm } from '../../components/nav';
import { useMe } from '../auth/api';
import { useMaterials } from '../materials/api';
import MaterialForm from '../materials/MaterialForm';
import { PHASE_TYPE_LABEL } from '../phases/types';
import {
  scanCode,
  useAssignBarcode,
  useCount,
  useCountBatch,
  useIssue,
  useIssueTargets,
  useLocations,
  useMove,
  useReceive,
  whereLabel,
  type ScanMaterial,
  type ScanResult,
  type StockLocation,
} from './api';

/**
 * Sklad v telefonu: naskenuj štítek (QR `MAT:`/`LOC:`) nebo čárový kód výrobce.
 * Režim určuje, co sken udělá — při příjmu dodávky nebo přestavbě regálu se pak
 * jen pípá a potvrzuje počet, bez klikání v menu.
 */

type Mode = 'info' | 'receive' | 'issue' | 'move' | 'count';

const MODES: Array<{ id: Mode; label: string; hint: string }> = [
  { id: 'info', label: 'Zjistit', hint: 'Co to je, kolik je skladem a kde leží.' },
  { id: 'receive', label: 'Příjem', hint: 'Naskladnit — naskenuj a zadej počet.' },
  { id: 'issue', label: 'Výdej', hint: 'Vydat na fázi (zakázku) nebo do režie.' },
  { id: 'move', label: 'Přesun', hint: 'Nejdřív naskenuj cílovou pozici, pak materiály.' },
  { id: 'count', label: 'Inventura', hint: 'Naskenuj materiál a zadej, kolik ho skutečně je — nebo naskenuj pozici.' },
];

const num = (s: string) => Number(String(s).replace(',', '.'));
// Čtečka posílá kód bez mezer a s číslicí; volný text se k materiálu jako kód nepřiřazuje.
const looksLikeCode = (s: string) => !/\s/.test(s) && /\d/.test(s);

interface LogEntry { at: number; text: string; ok: boolean }

function QtyForm({
  material,
  mode,
  canPrice,
  phaseId,
  onDone,
}: {
  material: ScanMaterial;
  mode: 'receive' | 'issue' | 'count';
  canPrice: boolean;
  phaseId: string;
  onDone: (text: string) => void;
}) {
  const fromOrder = mode === 'receive' && material.incomingQty > 0;
  const [qty, setQty] = useState(mode === 'count' ? String(Number(material.stockQty)) : fromOrder ? String(material.incomingQty) : '1');
  const [price, setPrice] = useState('');
  const receive = useReceive();
  const issue = useIssue();
  const count = useCount();
  const busy = receive.isPending || issue.isPending || count.isPending;
  const onError = (e: unknown) => toast.error((e as ApiError).message);
  const q = num(qty);
  const step = (d: number) => setQty(String(Math.max(0, Math.round(((num(qty) || 0) + d) * 1000) / 1000)));

  const submit = () => {
    if (!(q >= 0) || (mode !== 'count' && !(q > 0))) return;
    if (mode === 'receive') {
      receive.mutate(
        { id: material.id, qty: q, unitCost: canPrice && price.trim() ? num(price) : undefined },
        { onSuccess: (m) => onDone(`Příjem ${fmtNumber(q)} ${m.unit} — ${m.name} (skladem ${fmtNumber(Number(m.stockQty))})${m.fromOrder ? `, z objednávky ${fmtNumber(m.fromOrder)}` : ''}`), onError },
      );
    } else if (mode === 'issue') {
      issue.mutate(
        { id: material.id, qty: q, phaseId: phaseId || undefined },
        { onSuccess: (m) => onDone(`Výdej ${fmtNumber(q)} ${m.unit} — ${m.name}${phaseId ? '' : ' (režie)'} · zbývá ${fmtNumber(Number(m.stockQty))}`), onError },
      );
    } else {
      count.mutate(
        { id: material.id, newQty: q },
        { onSuccess: (m) => onDone(`Inventura ${m.name}: ${fmtNumber(q)} ${m.unit}`), onError },
      );
    }
  };

  const label = mode === 'receive' ? 'Přijmout' : mode === 'issue' ? 'Vydat' : 'Uložit stav';
  return (
    <div className="space-y-3">
      <div className="text-xs font-medium text-zinc-500">{mode === 'count' ? 'Skutečný stav' : 'Počet'} ({material.unit})</div>
      {fromOrder && <p className="text-sm text-sky-700 dark:text-sky-400">Čeká na doručení {fmtNumber(material.incomingQty)} {material.unit}. Příjem se odečte z objednávky.</p>}
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => step(-1)} className="h-14 w-14 rounded-xl border border-zinc-300 text-2xl dark:border-zinc-700">−</button>
        <input
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          inputMode="decimal"
          autoFocus
          onFocus={(e) => e.target.select()}
          className="h-14 min-w-0 flex-1 rounded-xl border border-zinc-300 bg-white text-center text-2xl font-semibold tabular-nums dark:border-zinc-700 dark:bg-zinc-900"
        />
        <button type="button" onClick={() => step(1)} className="h-14 w-14 rounded-xl border border-zinc-300 text-2xl dark:border-zinc-700">+</button>
      </div>
      {mode === 'receive' && canPrice && (
        <Input inputMode="decimal" placeholder="Nákupní cena / j (nepovinné — jinak za průměr)" value={price} onChange={(e) => setPrice(e.target.value)} />
      )}
      {mode === 'issue' && !phaseId && <p className="text-xs text-amber-700 dark:text-amber-400">Bez fáze = výdej do režie (nejde na žádnou zakázku).</p>}
      <button
        onClick={submit}
        disabled={busy}
        className="h-14 w-full rounded-xl bg-zinc-900 text-lg font-semibold text-white disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900"
      >
        {busy ? 'Ukládám…' : label}
      </button>
    </div>
  );
}

function MaterialCard({ m }: { m: ScanMaterial }) {
  const low = m.minQty != null && m.availableQty < Number(m.minQty);
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
      <div className="text-lg font-semibold leading-tight">{m.name}</div>
      <div className="mt-0.5 text-xs text-zinc-500">
        <span className="font-mono">{m.sku}</span>
        {m.barcode && <> · EAN {m.barcode}</>}
        {m.archivedAt && <span className="text-amber-700"> · archivovaný</span>}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
        <div className="rounded-lg bg-zinc-50 p-2 dark:bg-zinc-800/60">
          <div className="text-[11px] uppercase tracking-wide text-zinc-500">Skladem</div>
          <div className={`text-xl font-semibold tabular-nums ${low ? 'text-red-600 dark:text-red-400' : ''}`}>{fmtNumber(Number(m.stockQty))} {m.unit}</div>
          {m.reservedQty > 0 && <div className="text-[11px] text-sky-700 dark:text-sky-400">rezerv. {fmtNumber(m.reservedQty)} · volné {fmtNumber(m.availableQty)}</div>}
          {m.incomingQty > 0 && <div className="text-[11px] text-amber-700 dark:text-amber-400">čeká na doručení {fmtNumber(m.incomingQty)}</div>}
        </div>
        <div className="rounded-lg bg-zinc-50 p-2 dark:bg-zinc-800/60">
          <div className="text-[11px] uppercase tracking-wide text-zinc-500">Pozice</div>
          <div className="text-xl font-semibold">{whereLabel(m)}</div>
          {m.stockLocation?.name && <div className="text-[11px] text-zinc-500">{m.stockLocation.name}</div>}
        </div>
      </div>
    </div>
  );
}

function LocationCount({ location, materials, onDone }: { location: StockLocation; materials: ScanMaterial[]; onDone: (text: string) => void }) {
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(materials.map((m) => [m.id, String(Number(m.stockQty))])));
  const batch = useCountBatch();
  const save = () =>
    batch.mutate(
      { items: materials.map((m) => ({ materialId: m.id, newQty: num(values[m.id] ?? '0') || 0 })), note: `inventura pozice ${location.code}` },
      { onSuccess: (r) => onDone(`Inventura ${location.code}: ${r.changed} opraveno, ${r.unchanged} sedí`), onError: (e) => toast.error((e as ApiError).message) },
    );
  return (
    <div className="space-y-2">
      {materials.map((m) => (
        <div key={m.id} className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-sm">{m.name}</span>
          <input
            value={values[m.id] ?? ''}
            onChange={(e) => setValues((v) => ({ ...v, [m.id]: e.target.value }))}
            inputMode="decimal"
            className="h-10 w-24 rounded-lg border border-zinc-300 bg-white text-right text-sm tabular-nums dark:border-zinc-700 dark:bg-zinc-900"
          />
          <span className="w-8 text-xs text-zinc-500">{m.unit}</span>
        </div>
      ))}
      <Button className="w-full" disabled={batch.isPending} onClick={save}>{batch.isPending ? 'Ukládám…' : 'Uložit inventuru pozice'}</Button>
    </div>
  );
}

export default function WarehouseScanPage() {
  const { data: me } = useMe();
  const canPrice = !!me?.caps.includes('finance');
  const [mode, setMode] = useState<Mode>('info');
  const [result, setResult] = useState<ScanResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState<StockLocation | null>(null); // cíl přesunu
  const [phaseId, setPhaseId] = useState(''); // kam vydávat
  const [action, setAction] = useState<'receive' | 'issue' | 'count' | null>(null); // akce z režimu Zjistit
  const [countLocation, setCountLocation] = useState(false);
  const [log, setLog] = useState<LogEntry[]>([]);
  const [creating, setCreating] = useState<string | null>(null); // nový materiál s kódem
  const [pick, setPick] = useState('');
  const { data: locations } = useLocations();
  const { data: targets } = useIssueTargets(mode === 'issue' || action === 'issue');
  const { data: allMaterials } = useMaterials();
  const move = useMove();
  const assign = useAssignBarcode();

  const addLog = (text: string, ok = true) => setLog((l) => [{ at: Date.now(), text, ok }, ...l].slice(0, 12));
  const done = (text: string) => {
    addLog(text);
    toast.success(text);
    setAction(null);
    setCountLocation(false);
    setResult(null); // připraveno na další sken
  };

  const handle = async (code: string) => {
    setBusy(true);
    try {
      const r = await scanCode(code);
      setAction(null);
      setCountLocation(false);
      // Přesun: pozice = nový cíl, materiál = rovnou přesunout.
      if (mode === 'move') {
        if (r.type === 'location') {
          setTarget(r.location);
          setResult(r);
          addLog(`Cíl přesunu: ${r.location.code}`);
          return;
        }
        if (r.type === 'material' && target) {
          const m = await move.mutateAsync({ id: r.material.id, locationId: target.id });
          addLog(`${m.name} → ${target.code}`);
          toast.success(`${m.name} → ${target.code}`);
          setResult({ type: 'material', material: m });
          return;
        }
        if (r.type === 'material' && !target) toast.error('Nejdřív naskenuj cílovou pozici (štítek LOC na regálu).');
      }
      setResult(r);
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  const groupedTargets = useMemo(() => targets ?? [], [targets]);
  const qtyMode: 'receive' | 'issue' | 'count' | null = action ?? (mode === 'receive' || mode === 'issue' || mode === 'count' ? mode : null);

  // Výběr materiálu pro přiřazení neznámého kódu (hledání podle názvu / SKU).
  const matches = useMemo(() => {
    const q = norm(pick);
    if (!q) return [];
    return (allMaterials ?? []).filter((m) => norm(`${m.name} ${m.sku}`).includes(q)).slice(0, 8);
  }, [pick, allMaterials]);

  return (
    <div className="mx-auto max-w-xl">
      <h1 className="text-2xl font-bold tracking-tight">Sklad — skener</h1>
      <p className="mb-3 text-sm text-zinc-500">{MODES.find((m) => m.id === mode)?.hint}</p>

      <div role="tablist" className="mb-3 grid grid-cols-5 gap-1 rounded-xl bg-zinc-100 p-1 dark:bg-zinc-900">
        {MODES.map((m) => (
          <button
            key={m.id}
            role="tab"
            aria-selected={mode === m.id}
            onClick={() => { setMode(m.id); setResult(null); setAction(null); }}
            className={`rounded-lg px-1 py-2 text-xs font-medium sm:text-sm ${mode === m.id ? 'bg-white shadow-sm dark:bg-zinc-800' : 'text-zinc-500'}`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {mode === 'move' && (
        <div className="mb-3 flex items-center gap-2 rounded-lg bg-sky-50 p-2 text-sm dark:bg-sky-950/30">
          <span className="shrink-0 text-sky-900 dark:text-sky-200">Cíl:</span>
          <Select value={target?.id ?? ''} onChange={(e) => setTarget(locations?.find((l) => l.id === e.target.value) ?? null)} className="h-9 text-sm">
            <option value="">— naskenuj nebo vyber pozici —</option>
            {(locations ?? []).map((l) => <option key={l.id} value={l.id}>{l.code}{l.name ? ` · ${l.name}` : ''}</option>)}
          </Select>
        </div>
      )}
      {(mode === 'issue' || action === 'issue') && (
        <div className="mb-3 flex items-center gap-2 rounded-lg bg-amber-50 p-2 text-sm dark:bg-amber-950/30">
          <span className="shrink-0 text-amber-900 dark:text-amber-200">Kam:</span>
          <Select value={phaseId} onChange={(e) => setPhaseId(e.target.value)} className="h-9 text-sm">
            <option value="">bez zakázky (režie)</option>
            {groupedTargets.map((t) => (
              <option key={t.id} value={t.id}>
                {t.mine ? '★ ' : ''}{t.projectCode} · {t.propItemName} → {PHASE_TYPE_LABEL[t.type]}
              </option>
            ))}
          </Select>
        </div>
      )}

      <BarcodeScanner onCode={handle} paused={busy || !!qtyMode && result?.type === 'material'} />

      <div className="mt-4 space-y-3">
        {result?.type === 'material' && (
          <>
            <MaterialCard m={result.material} />
            {qtyMode ? (
              <QtyForm key={`${result.material.id}-${qtyMode}`} material={result.material} mode={qtyMode} canPrice={canPrice} phaseId={phaseId} onDone={done} />
            ) : mode === 'info' ? (
              <div className="grid grid-cols-2 gap-2">
                <Button variant="secondary" className="h-12" onClick={() => setAction('receive')}>+ Příjem</Button>
                <Button variant="secondary" className="h-12" onClick={() => setAction('issue')}>− Výdej</Button>
                <Button variant="secondary" className="h-12" onClick={() => setAction('count')}>Inventura</Button>
                <Button variant="secondary" className="h-12" onClick={() => { setMode('move'); setResult(null); }}>Přesunout…</Button>
              </div>
            ) : null}
            {(mode === 'info' || qtyMode) && (
              <button onClick={() => { setResult(null); setAction(null); }} className="w-full text-center text-sm text-zinc-500 underline">Další sken</button>
            )}
          </>
        )}

        {result?.type === 'location' && (
          <div className="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold">{result.location.code}</span>
              {result.location.zone && <span className="text-xs uppercase text-zinc-500">{result.location.zone}</span>}
            </div>
            {result.location.name && <div className="text-sm text-zinc-500">{result.location.name}</div>}
            {mode === 'move' ? (
              <p className="mt-2 text-sm text-sky-700 dark:text-sky-400">Cíl nastaven — teď skenuj materiály, které sem přesouváš.</p>
            ) : countLocation ? (
              <div className="mt-3"><LocationCount location={result.location} materials={result.materials} onDone={done} /></div>
            ) : (
              <>
                <ul className="mt-3 divide-y divide-zinc-100 text-sm dark:divide-zinc-800">
                  {result.materials.map((m) => (
                    <li key={m.id} className="flex justify-between gap-2 py-1.5">
                      <span className="truncate">{m.name}</span>
                      <span className="shrink-0 tabular-nums text-zinc-600 dark:text-zinc-300">{fmtNumber(Number(m.stockQty))} {m.unit}</span>
                    </li>
                  ))}
                  {result.materials.length === 0 && <li className="py-1.5 text-zinc-400">Na pozici zatím nic není.</li>}
                </ul>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {result.materials.length > 0 && <Button variant="secondary" onClick={() => setCountLocation(true)}>Inventura pozice</Button>}
                  <Button variant="secondary" onClick={() => { setMode('move'); setTarget(result.location); }}>Přesouvat sem</Button>
                </div>
              </>
            )}
          </div>
        )}

        {result?.type === 'search' && (
          <div className="rounded-xl border border-zinc-200 bg-white p-2 dark:border-zinc-800 dark:bg-zinc-900">
            <div className="px-2 py-1 text-xs font-medium text-zinc-500">Nalezeno podle názvu: {result.code}</div>
            <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
              {result.materials.map((m) => (
                <li key={m.id}>
                  <button onClick={() => handle(`MAT:${m.sku}`)} className="flex min-h-12 w-full items-center justify-between gap-3 rounded-lg px-2 py-2 text-left text-sm hover:bg-zinc-50 dark:hover:bg-zinc-800">
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{m.name}</span>
                      <span className="block text-xs text-zinc-500">{whereLabel(m)} · <span className="font-mono">{m.sku}</span></span>
                    </span>
                    <span className="shrink-0 tabular-nums">{fmtNumber(m.availableQty)} {m.unit}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {result?.type === 'unknown' && !looksLikeCode(result.code) && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30">
            <div className="font-semibold text-amber-900 dark:text-amber-200">Nic takového ve skladu není</div>
            <div className="text-sm">„{result.code}“ není kód ani část názvu žádného materiálu.</div>
          </div>
        )}

        {result?.type === 'unknown' && looksLikeCode(result.code) && (
          <div className="space-y-3 rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30">
            <div>
              <div className="font-semibold text-amber-900 dark:text-amber-200">Neznámý kód</div>
              <div className="font-mono text-sm">{result.code}</div>
            </div>
            <div>
              <div className="mb-1 text-xs font-medium text-zinc-600 dark:text-zinc-300">Přiřadit k materiálu</div>
              <Input placeholder="hledej název nebo SKU" value={pick} onChange={(e) => setPick(e.target.value)} />
              <div className="mt-1 space-y-1">
                {matches.map((m) => (
                  <button
                    key={m.id}
                    onClick={() =>
                      assign.mutate(
                        { id: m.id, barcode: result.code },
                        {
                          onSuccess: (mm) => { addLog(`Kód ${result.code} → ${mm.name}`); toast.success('Kód přiřazen'); setPick(''); setResult({ type: 'material', material: mm }); },
                          onError: (e) => toast.error((e as ApiError).message),
                        },
                      )
                    }
                    className="block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-left text-sm hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900"
                  >
                    {m.name} <span className="font-mono text-xs text-zinc-400">{m.sku}</span>
                  </button>
                ))}
              </div>
            </div>
            <Button variant="secondary" className="w-full" onClick={() => setCreating(result.code)}>+ Nový materiál s tímto kódem</Button>
          </div>
        )}

        {log.length > 0 && (
          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">Naposledy</div>
            <ul className="space-y-1 text-xs">
              {log.map((l) => (
                <li key={l.at} className={l.ok ? 'text-zinc-600 dark:text-zinc-300' : 'text-red-600'}>
                  {new Date(l.at).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })} · {l.text}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {creating && (
        <MaterialForm
          material={null}
          initialBarcode={creating}
          onClose={() => setCreating(null)}
          onCreated={async (m) => {
            addLog(`Nový materiál ${m.name}`);
            const r = await scanCode(m.sku).catch(() => null);
            if (r) setResult(r);
          }}
        />
      )}
    </div>
  );
}
